<template>
  <button
    class="FormButton"
    type="button"
    :style="{
      '--backgroundColor': backgroundColor,
      '--textColor': textColor,
    }"
    :data-variant="variant"
    :data-size="size"
    :data-shape="shape"
    :data-orientation="orientation"
    :data-direction="direction || undefined"
    :data-logo="logoName || undefined"
    @click="haptic()"
  >
    <span class="FormButton__label">
      <template v-if="logoName || iconName">
        <Logo
          v-if="logoName"
          class="FormButton__logo"
          :name="logoName"
        />
        <Icon
          v-else-if="iconName"
          class="FormButton__icon"
          :size="orientation === 'horizontal' ? 'large' : 'xlarge'"
          :name="iconName"
        />
        <Transition>
          <slot />
        </Transition>
      </template>
      <template v-else>
        <Transition>
          <slot />
        </Transition>
      </template>
    </span>
  </button>
</template>

<script lang="ts" setup>
import haptic from "@/lib/utils/platform/haptic";
import { IconName } from "./Icon.types";
import Icon from "./Icon.vue";
import type { LogoName } from "./Logo.types";
import Logo from "./Logo.vue";

const {
  variant = "primary",
  iconName,
  size = "medium",
  logoName,
  shape = "square",
  backgroundColor,
  textColor,
  orientation = "horizontal",
  direction = null,
} = defineProps<{
  variant?: "primary" | "destructive",
  iconName?: keyof typeof IconName,
  size?: "small" | "medium",
  shape?: "round" | "square",
  logoName?: LogoName,
  backgroundColor?: string,
  textColor?: string,
  orientation?: "horizontal" | "vertical",
  direction?: "left" | "right" | null,
}>();
</script>

<style lang="scss" scoped>
.FormButton {
  --buttonHeight: 48px;
  --highlightWidth: 3px;
  --buttonOuterHeight: calc(var(--buttonHeight) + 2 * var(--highlightWidth));
  --outlineWidth: 2px;
  --offsetWidth: calc(var(--highlightWidth) + var(--outlineWidth));
  --borderRadius: calc(6px + var(--offsetWidth));
  --paddingInline: 24px;
  --outlineColorDisabled: var(--themeDarkTransparent400);
  --backgroundColorDisabled: var(--themeLightTransparent400);
  --textColorDisabled: var(--themeDarkTransparent500);
  --highlightColorDisabled: var(--themeLightTransparent300);

  [data-theme="light"] & {
    --highlightWidth: 0px;
    --outlineColorDisabled: var(--themeColorNeutral400);
    --backgroundColorDisabled: var(--themeColorNeutral150);
    --textColorDisabled: var(--themeColorNeutral500);
    --highlightColorDisabled: var(--themeColorNeutral0);

    box-shadow: 0 0 0 0px var(--themeColorSecondary);
  }

  &[data-variant="destructive"] {
    --backgroundColor: var(--themeColorPrimary);
    --textColor: var(--themeColorPrimaryForeground);
  }

  position: relative;

  width: 100%;

  border-radius: var(--borderRadius);
  outline: var(--outlineWidth) solid var(--themeColorNeutral900);
  outline-offset: calc(-1 * var(--offsetWidth));
  background-color: var(--backgroundColor, var(--themeColorSecondary));
  color: var(--textColor, var(--themeColorSecondaryForeground));
  box-shadow: 0 0 0 var(--highlightWidth) var(--themeColorNeutral0) inset;

  font: var(--fontWeightMedium) 18px / 1 var(--fontFamilyNormal);
  font-stretch: var(--fontWidthExtended);

  transition: 350ms;
  transition-property: background-color, color, outline-color, box-shadow;

  &:disabled {
    outline-color: var(--outlineColorDisabled);
    background-color: var(--backgroundColorDisabled);
    color: var(--textColorDisabled);
    box-shadow: 0 0 0 var(--highlightWidth) var(--highlightColorDisabled) inset;
  }

  &:has(.FormButton__logo) {
    --paddingInline: 12px;
  }

  &:has(.FormButton__icon) {
    --paddingInline: 14px;
  }

  &:focus-visible {
    box-shadow: 0 0 0 var(--highlightWidth) var(--themeColorSecondary) inset;

    [data-theme="light"] & {
      box-shadow: 0 0 0 3px var(--themeColorSecondary);
    }
  }

  &[data-size="small"] {
    --borderRadius: calc(4px + var(--offsetWidth));
    --buttonHeight: 36px;
    --paddingInline: 18px;
    font-size: 14px;
  }

  &[data-shape="round"] {
    --highlightWidth: 2px;
    --borderRadius: 9999px;
  }
}

.FormButton__label {
  position: relative;
  display: block;
  padding: 0 calc(var(--paddingInline) + var(--offsetWidth));

  .FormButton[data-orientation="horizontal"] & {
    height: var(--buttonOuterHeight);
    overflow: hidden;

    mask-image: linear-gradient(
      to bottom,
      transparent 0,
      black 8px,
      black calc(100% - 8px),
      transparent 100%
    );

    line-height: var(--buttonOuterHeight);
    white-space: nowrap;

    &:has(.FormButton__logo),
    &:has(.FormButton__icon) {
      display: grid;
      grid: auto / 1fr auto 1fr;
      align-items: center;
    }
  }

  .FormButton[data-orientation="vertical"] & {
    display: flex;
    flex-direction: column;
    gap: 12px;
    justify-content: center;
    align-items: center;
    padding-block: 24px;

    line-height: 1.1;
  }

  .v-enter-active,
  .v-leave-active {
    display: block;
    transition: 350ms;
    transition-property: transform, opacity;
  }

  .v-leave-active {
    position: absolute;
    inset: 0;

    display: grid;
    place-items: center;
  }

  .FormButton[data-direction="left"] & .v-enter-active,
  .FormButton[data-direction="left"] & .v-leave-active,
  .FormButton[data-direction="right"] & .v-enter-active,
  .FormButton[data-direction="right"] & .v-leave-active {
    transition-duration: 150ms;
  }

  .v-enter-from {
    transform: translateY(var(--buttonOuterHeight));
  }

  .v-leave-to {
    transform: translateY(calc(-1 * var(--buttonOuterHeight)));
  }

  .FormButton[data-direction="left"] & .v-enter-from,
  .FormButton[data-direction="right"] & .v-leave-to {
    transform: translateX(-24px);
    opacity: 0;
  }

  .FormButton[data-direction="right"] & .v-enter-from,
  .FormButton[data-direction="left"] & .v-leave-to {
    transform: translateX(24px);
    opacity: 0;
  }
}

.FormButton__logo {
  .FormButton[data-logo="apple"] & {
    position: relative;
    top: -2px;
  }

  .FormButton[data-orientation="horizontal"] & {
    margin: -6px 0;
  }

  .FormButton:disabled & {
    --fillColor: var(--themeColorNeutral500);
  }
}

.FormButton__icon {
  .FormButton[data-orientation="horizontal"] & {
    margin: -10px 0;
  }
}
</style>
